export enum CAMPAIGN_TYPE {
  VIDEO = 'video',
  IMAGE = 'image',
  //   SOCIAL = 'social',
}

export const isCampaignType = (
  campaignType: any
): campaignType is CAMPAIGN_TYPE => {
  return (
    typeof campaignType === 'string' &&
    (Object.values(CAMPAIGN_TYPE) as string[]).includes(campaignType)
  );
};

export enum GENDER {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
}

export const PRODUCT_CATEGORIES = [
  'wellness',
  'food',
  'beverages',
  'fashion',
  'beauty',
  'adventure',
  'home',
  'fitness',
  'pet',
  'technology',
  'travel',
  'other',
] as const;

export type PRODUCT_CATEGORY = (typeof PRODUCT_CATEGORIES)[number];

export enum HEALTH_STATUS {
  IN_RANGE = 'in range',
  POOR = 'poor',
  GREAT = 'great',
}

export interface ProductApplication {
  status: string; // ?
  timestamp: string;
}

export enum PRODUCT_STATUS {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
  DRAFT = 'draft',
}

type CreatorUID = string;

export enum VIDEO_LENGTH {
  FIFTEEN = '15seconds',
  THIRTY = '15-30seconds',
  SIXTY = '60seconds',
}

export enum CONTENT_LAYOUT_STYLE {
  SQUARE = 'square',
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export enum STYLE_CATEGORY {
  PRODUCT_PHOTOGRAPHY = 'productPhotography',
  LIFE_STYLE_OR_ACTION_PHOTOGRAPHY = 'lifestyleOrActionPhotography',
  PORTRAIT_STYLE_PHOTOGRAPHY = 'portraitStylePhotography',
  ACTION_OR_PRODUCT_IN_USE_VIDEO = 'actionOrProductInUseVideo',
  UNBOXING_EXPLANATION_VIDEO = 'unboxingExplanationVideo',
  PRODUCT_REVIEW_OR_TESTIMONIAL_VIDEO = 'productReviewOrTestimonialVideo',
  OTHER = 'other',
}

export enum CONTENT_PLATFORM {
  INSTAGRAM = 'instagram',
  TIKTOK = 'tiktok',
  FACEBOOK = 'facebook',
  YOUTUBE = 'youtube',
  AMAZON = 'amazon',
  GENERAL_USE = 'generalUse',
}

export interface Product {
  brandUID: string;
  createdAt: number;
  updatedAt: number;
  draftStep?: number;

  status?: PRODUCT_STATUS;
  name?: string;
  type?: CAMPAIGN_TYPE;
  isListed?: boolean;
  appliedUID?: Record<CreatorUID, ProductApplication>;
  health?: HEALTH_STATUS;
  category?: PRODUCT_CATEGORY;
  downloadCount?: number;
  isOnLikeToKnowIt?: boolean;
  applicantHealth?: number;
  postOverdueHealth?: number;
  brandtags?: Record<string, string>;
  hashtags?: Record<string, string>;
  image?: string;
  shippedHealth?: number;
  postOverdueCount?: number;
  numberOfPosts?: number;
  states?: Record<string, string>;
  url?: string;
  guidelines?: string;
  shippedCount?: number;
  postCount?: number;
  lastCampaignStatsUpdate?: number;
  applicantCount?: number;
  downloadHealth?: number;
  reverseEpochTimestamp?: number;
  influencerCount?: number;
  lastLoginHealth?: number;
  influencerHealth?: number;
  needsShippingCount?: number;
  brandActive?: boolean;
  lowercaseName?: string;
  isVIP?: boolean;
  value?: number;
  filters?: Record<string, string>;
  completedPostCount?: number;
  postGuidelines?: string[];
  styleGuide?: Record<string, any>; // Example images
  styleCategory?: STYLE_CATEGORY[];
  styleGuideLink?: string[];
  videoDuration?: VIDEO_LENGTH;
  contentLayout?: CONTENT_LAYOUT_STYLE;
  gender?: GENDER | null;
  platform?: CONTENT_PLATFORM;
}

export type Campaign = Required<
  Pick<
    Product,
    | 'brandUID'
    | 'category'
    | 'platform'
    | 'styleCategory'
    | 'gender'
    | 'name'
    | 'url'
    | 'value'
    // | 'draftStep'
  >
> &
  Pick<
    Product,
    | 'type'
    | 'createdAt'
    | 'updatedAt'
    | 'isListed'
    | 'status'
    | 'updatedAt'
    | 'contentLayout'
    | 'videoDuration'
    | 'postGuidelines' // [0] is Product description, [1] is Content Style Request, [2] is Caption details
    | 'image'
    // Present in previous brief builder, may need to have defaults
    // Social Fields
    // | 'brandtags'
    // | 'hashtags'
    // | 'styleGuide'
    // | 'styleGuideLink'
  > & {
    uid: string;
  };

export type CampaignFormikValues = Omit<
  Campaign,
  'brandUID' | 'uid' | 'createdAt' | 'updatedAt' | 'isListed' | 'status'
> & {
  // Only collected on 1st campaign
  userFullName?: string;
  companyName?: string;
};
